import {
  Component,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SupervisorApiService } from '../../core/api/services';
import { SupervisorHelperService } from '../supervisor.helper.service';
import { AlertService } from '../../shared/services/alert.service';
import { GeneralAttachmentModel, PatientInstructionsModel, PatientModel } from '../../core/api/models';
import { AddAttachmentModalComponent } from '../../shared/components/add-attachment/add-attachment-modal/add-attachment-modal.component';
import { AttachmentViewComponent } from '../../shared/components/attachment-view/attachment-view.component';
import { SharedModule } from '../../shared/shared.module';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
// CKEditor imports
import { Editor, editorConfig } from '../../shared/ckeditor/ckeditor-config';

@Component({
  selector: 'app-instructions',
  templateUrl: './instructions.component.html',
  styleUrl: './instructions.component.css',
  standalone: false,
})
export class InstructionsComponent implements OnInit {
  instructions = signal('');
  instructionsID: number = 0;
  selectedPatient: PatientModel | null = null;
  isLoading = signal(false);
  isSaving = signal(false);
  patientIDAsNumber: number = 0;
  isLoadingAttachments: boolean = false;
  showAddAttachments: boolean = false;
  doShowAttachmentView: boolean = false;
  addAttachmentView: string = "patientInstructions";
  attachmentList: GeneralAttachmentModel[] = [];
  selectedAttachment: GeneralAttachmentModel | null = null;
  // Using the ClassicEditor from the imported CKEditor module
  public Editor = Editor;

  initialInstructions: string = '';

  public editorConfig = editorConfig;

  constructor(
    private route: ActivatedRoute,
    private supervisorApiService: SupervisorApiService,
    private supervisorHelperService: SupervisorHelperService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.route.parent?.params.subscribe(params => {
      const patientID = params['patientID'];
      this.patientIDAsNumber = +patientID;
      
      // Get the parent component (SupervisorMainComponent) to get the selectedPatient
      const parentComponent = this.route.parent?.component;
      if (parentComponent) {
        const mainComponent = parentComponent as any;
        if (mainComponent.selectedPatient) {
          this.selectedPatient = mainComponent.selectedPatient;
        }
      }
      
      // If we have the patientID, we can load the instructions even without selectedPatient object
      this.loadPatientInstructions();
    });
  }

  loadPatientInstructions(): void {
    if (!this.patientIDAsNumber) {
      return;
    }

    this.isLoading.set(true);

    this.supervisorApiService.GetPatientInstructionAttachments({
      patientID: this.patientIDAsNumber
    }).subscribe({
      next: (results) => {
        this.attachmentList = results;
      },
      error: (error) => {
        console.error('Error loading patient instruction attachments', error);
        this.alertService.error('Failed to load patient instruction attachments');
        this.isLoading.set(false);
      }
    });
    
    this.supervisorApiService.GetPatientInstructions({
      patientID: this.patientIDAsNumber
    }).subscribe({
      next: (results) => {
        this.initialInstructions = results.Instructions || '';
        this.instructions.set(results.Instructions || '');
        this.instructionsID = results.ID ?? 0;
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading patient instructions', error);
        this.alertService.error('Failed to load patient instructions');
        this.isLoading.set(false);
      }
    });
  }

  onInstructionsChange(event: any): void {
    if (event.editor) {
      const data = event.editor.getData();
      this.instructions.set(data);
    }
  }

  toggleShowAddAttachments() : void {
    this.showAddAttachments = !this.showAddAttachments;
  }

  openAttachmentView(att: GeneralAttachmentModel) : void {
    this.doShowAttachmentView = true;
    this.selectedAttachment = att;
  }

  getImageUrl(att: GeneralAttachmentModel) : string {
    var url = 'data:'+ att.FileType +';base64,'+ att.ImageThumbnail;
    return url;
  }

  closeAddAttachments() {
    this.showAddAttachments = false;
    this.loadAttachments();
  }

  loadAttachments(){
    this.isLoadingAttachments = true;
    this.supervisorApiService.GetPatientInstructionAttachments({
      patientID: this.patientIDAsNumber
    }).subscribe({
      next: (results) => {
        this.attachmentList = results;
        this.isLoadingAttachments = false;
      },
      error: (error) => {
        console.error('Error loading patient instruction attachments', error);
        this.alertService.error('Failed to load patient instruction attachments');
        this.isLoadingAttachments = false;
      }
    });
  }

  closeAttachmentView() {
    this.doShowAttachmentView = false;
    this.selectedAttachment = null;
    this.loadAttachments();
  }
  
  saveInstructions(): void {
    if (!this.patientIDAsNumber) {
      return;
    }

    this.isSaving.set(true);
    
    this.supervisorApiService.SavePatientInstructions({
      patientID: this.patientIDAsNumber,
      body: {
        ID: this.instructionsID,
        Instructions: this.instructions()
      }
    }).subscribe({
      next: () => {
        this.alertService.success('Patient instructions saved successfully');
        this.isSaving.set(false);
      },
      error: (error) => {
        console.error('Error saving patient instructions', error);
        this.alertService.error('Failed to save patient instructions');
        this.isSaving.set(false);
      }
    });
  }
}
